import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import UserCard from "./UserCard";
import ProfileCard from "./ProfileCard";
import ProfileCoverUploader from "./ProfileCoverUploader";
import AvatarUploader from "./AvatarUploader";
import "../../css/components/Cabinet.css";
import {usersApi, notificationsApi, battlePersonApi} from "../../api";
import CreateProfilePopup from "./CreateProfilePopup";
import MyBattlePersons from "./MyBattlePersons";
import HrnbButton from "../controls/HrnbButton";
import HrnbSwitch from "../controls/HrnbSwitch";
import HrnbLoader from "../controls/HrnbLoader";
import HrnbCard from "../controls/HrnbCard";
import {HrnbTab, HrnbTabs} from "../controls/HrnbTabs";
import HrnbAlert from "../controls/HrnbAlert";
import {CreateProfileIcon, SaveIcon, BattlesIcon, NotificationIcon} from "../../icons";

const NOTIFICATION_GROUPS = [
    {
        label: 'Баттлы',
        types: [
            { value: 'BATTLE_STARTED', label: 'Начало баттла' },
            { value: 'ROUND_STARTED', label: 'Начало раунда' },
            { value: 'ROUND_ENDED', label: 'Окончание раунда' },
            { value: 'BATTLE_INVITE', label: 'Приглашение в баттл' },
            { value: 'BATTLE_APPLICATION', label: 'Заявка в баттл' },
            { value: 'BATTLE_PUBLISHED', label: 'Публикация баттла' },
            { value: 'APPLICATION_APPROVED', label: 'Заявка одобрена' },
            { value: 'APPLICATION_REJECTED', label: 'Заявка отклонена' },
            { value: 'VOTING_STARTED', label: 'Начало голосования' },
            { value: 'TRACK_DISQUALIFIED', label: 'Трек дисквалифицирован' },
        ],
    },
    {
        label: 'Дуэли',
        types: [
            { value: 'DUEL_INVITE', label: 'Приглашение в дуэль' },
            { value: 'DUEL_STARTED', label: 'Начало дуэли' },
            { value: 'DUEL_ROUND_STARTED', label: 'Начало раунда дуэли' },
            { value: 'DUEL_TRACK_SUBMITTED', label: 'Трек отправлен в дуэли' },
            { value: 'DUEL_ROUND_ENDED', label: 'Окончание раунда дуэли' },
            { value: 'DUEL_FINISHED', label: 'Завершение дуэли' },
            { value: 'DUEL_QUICK_MATCH_FOUND', label: 'Быстрый матч найден' },
            { value: 'DUEL_JUDGE_INVITE', label: 'Приглашение судьёй' },
            { value: 'DUEL_STATUS_CHANGED', label: 'Статус дуэли изменён' },
            { value: 'DUEL_CANCELLED', label: 'Дуэль отменена' },
            { value: 'DUEL_PARTICIPANT_KICKED', label: 'Исключение из дуэли' },
            { value: 'DUEL_APPEAL_SUBMITTED', label: 'Апелляция подана' },
            { value: 'DUEL_APPEAL_REVIEWED', label: 'Апелляция рассмотрена' },
            { value: 'DUEL_CHAT_MESSAGE', label: 'Сообщение в чате дуэли' },
        ],
    },
    {
        label: 'Форум и чаты',
        types: [
            { value: 'FORUM_COMMENT', label: 'Комментарий на форуме' },
            { value: 'CHAT_MESSAGE', label: 'Сообщение в чате' },
        ],
    },
    {
        label: 'Прочее',
        types: [
            { value: 'NEW_FOLLOWER', label: 'Новый подписчик' },
            { value: 'EVENT_REMINDER', label: 'Напоминание о событии' },
            { value: 'SYSTEM', label: 'Системные уведомления' },
        ],
    },
];

const Cabinet = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Статистика
    const [stats, setStats] = useState({ battleCount: 0, unreadNotifications: 0 });
    const [statsLoading, setStatsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") || "personal-info";
    const handleTabChange = useCallback((tab) => {
        if (tab !== activeTab && isDirty && !window.confirm('У вас есть несохранённые изменения. Переключить вкладку?')) {
            return;
        }
        setSearchParams(tab === "personal-info" ? {} : { tab }, { replace: true });
    }, [activeTab, isDirty, setSearchParams]);
    const notificationsFetchedRef = useRef(false);

    // State для настроек уведомлений (каналы)
    const [channelSettings, setChannelSettings] = useState({
        emailNotificationsEnabled: true,
        pushNotificationsEnabled: true,
        inAppNotificationsEnabled: true
    });
    const [notificationPreferences, setNotificationPreferences] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [notificationsSaving, setNotificationsSaving] = useState(false);
    const [notificationsSuccess, setNotificationsSuccess] = useState(null);
    const [notificationsError, setNotificationsError] = useState(null);

    useEffect(() => {
        let mounted = true;

        const fetchUser = async () => {
            setLoading(true);
            setError(null);

            try {
                const res = await usersApi.current();
                // Поддерживаем оба варианта: { data: { ... } } и сразу data
                const data = res && (res.data ?? res);

                if (!data) {
                    throw new Error("Нет данных пользователя");
                }

                if (!mounted) return;

                setCurrentUser(data);

                const firstProfile =
                    Array.isArray(data.profiles) && data.profiles.length > 0
                        ? data.profiles[0]
                        : null;
                setProfile(firstProfile);
            } catch (err) {
                if (!mounted) return;
                setError("Ошибка загрузки пользователя");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchUser();

        // Загрузка статистики
        const fetchStats = async () => {
            try {
                const [unreadCount, battlePersons] = await Promise.allSettled([
                    notificationsApi.getUnreadCount(),
                    battlePersonApi.list({ size: 1, status: 'APPROVED' })
                ]);
                if (!mounted) return;
                setStats({
                    unreadNotifications: unreadCount.status === 'fulfilled' ? unreadCount.value : 0,
                    battleCount: battlePersons.status === 'fulfilled'
                        ? (battlePersons.value?.totalElements ?? 0)
                        : 0,
                });
            } catch {
                // ignore
            } finally {
                if (mounted) setStatsLoading(false);
            }
        };
        fetchStats();

        return () => {
            mounted = false;
        };
    }, []);

    // Ленивая загрузка настроек уведомлений — только при первом открытии вкладки "Настройки"
    useEffect(() => {
        if (activeTab !== 'settings' || notificationsFetchedRef.current) return;
        notificationsFetchedRef.current = true;
        setNotificationsLoading(true);

        let mounted = true;
        const fetchNotificationSettings = async () => {
            try {
                const [settings, preferences] = await Promise.all([
                    usersApi.getSettings(),
                    notificationsApi.getPreferences()
                ]);
                if (!mounted) return;
                setChannelSettings(settings);
                setNotificationPreferences(Array.isArray(preferences) ? preferences : []);
            } catch (err) {
                console.error('Failed to load notification settings:', err);
            } finally {
                if (mounted) setNotificationsLoading(false);
            }
        };
        fetchNotificationSettings();

        return () => { mounted = false; };
    }, [activeTab]);

    // Обработчики для настроек уведомлений (каналы)
    const handleChannelToggle = (key) => {
        setChannelSettings(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Гранулярное переключение типа уведомления для канала
    const handleTypeToggle = (channel, type) => {
        setNotificationPreferences(prev => {
            const pref = prev.find(p => p.channel === channel);
            if (!pref) return prev;

            const isEnabled = (pref.enabledTypes || []).includes(type);
            const newTypes = isEnabled
                ? (pref.enabledTypes || []).filter(t => t !== type)
                : [...(pref.enabledTypes || []), type];

            return prev.map(p =>
                p.channel === channel ? { ...p, enabledTypes: newTypes } : p
            );
        });
    };

    const handleSaveNotifications = async () => {
        setNotificationsSaving(true);
        setNotificationsSuccess(null);
        setNotificationsError(null);

        try {
            // 1. Сохраняем глобальные настройки каналов
            await usersApi.updateSettings(channelSettings);

            // 2. Сохраняем гранулярные предпочтения
            const updatePromises = notificationPreferences.map(pref =>
                notificationsApi.updatePreferences({
                    channel: pref.channel,
                    enabledTypes: pref.enabledTypes || []
                })
            );
            await Promise.all(updatePromises);

            setNotificationsSuccess('Настройки уведомлений сохранены!');
        } catch (err) {
            console.error('Ошибка при сохранении настроек:', err);
            setNotificationsError(
                err.response?.data?.message || 'Ошибка при сохранении настроек уведомлений'
            );
        } finally {
            setNotificationsSaving(false);
        }
    };

    if (loading) return (
        <div className="cabinet-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh'}}>
            <HrnbLoader text="Загрузка..." />
        </div>
    );
    if (error) return (
        <div className="cabinet-container" style={{padding: '40px'}}>
            <HrnbAlert variant="danger" onClose={() => setError(null)}>{error}</HrnbAlert>
        </div>
    );
    if (!currentUser) return (
        <div className="cabinet-container" style={{padding: '40px'}}>
            <HrnbAlert variant="warning">Пользователь не найден</HrnbAlert>
        </div>
    );

    const openPopup = () => {
        setIsOpen(true);
    };

    const closePopup = () => {
        setIsOpen(false);
    };

    const updateProfile = (newProfile) => {
        setProfile(newProfile);
    };

    return (
        <div className="cabinet-container">
            <div className="cabinet-row">
                <div className="avatarside">
                    {profile ? (
                        <AvatarUploader profileId={profile.id} onAvatarUpdate={(updated) => setProfile(prev => ({ ...prev, ...updated }))}/>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#8a9aaa', padding: '24px 16px' }}>
                            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }}>👤</div>
                            <p style={{ fontSize: 14, marginBottom: 12 }}>Создайте профиль,<br/>чтобы загрузить аватар</p>
                            <HrnbButton onClick={openPopup} icon={<CreateProfileIcon />}>
                                Создать профиль
                            </HrnbButton>
                        </div>
                    )}
                </div>
                <div className="tabs-content">
                    <div className="tabs">
                        <HrnbTabs value={activeTab}
                                  onChange={handleTabChange}
                                  className="tabs.header">
                            <HrnbTab
                                label="Личная информация"
                                value="personal-info"/>
                            <HrnbTab
                                label="Публичный профиль"
                                value="public-profile"/>
                            <HrnbTab
                                label="Мои баттлы"
                                value="my-battles"/>
                            <HrnbTab
                                label="Настройки"
                                value="settings"/>
                        </HrnbTabs>
                        <div className="tabs.body">
                            {activeTab === "personal-info" && (
                                <div className="tab-pane">
                                    {!statsLoading && (
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                                            <HrnbCard className="stat-card" style={{ flex: '1 1 200px', minWidth: 200 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <BattlesIcon style={{ width: 32, height: 32, opacity: 0.7 }} />
                                                    <div>
                                                        <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.battleCount}</div>
                                                        <div style={{ fontSize: 13, color: '#8a9aaa' }}>Активных баттлов</div>
                                                    </div>
                                                </div>
                                            </HrnbCard>
                                            <HrnbCard className="stat-card" style={{ flex: '1 1 200px', minWidth: 200 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <NotificationIcon style={{ width: 32, height: 32, opacity: 0.7 }} />
                                                    <div>
                                                        <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.unreadNotifications}</div>
                                                        <div style={{ fontSize: 13, color: '#8a9aaa' }}>Непрочитанных</div>
                                                    </div>
                                                </div>
                                            </HrnbCard>
                                        </div>
                                    )}
                                    <UserCard user={currentUser} onDirtyChange={setIsDirty}/>
                                </div>
                            )}
                            {activeTab === "public-profile" && (
                                <div className="tab-pane">
                                    {profile ? (
                                        <>
                                            <ProfileCoverUploader
                                                profileId={profile.id}
                                                currentCoverUrl={profile.coverUrl || null}
                                                onCoverUpdate={(url) => setProfile(prev => ({ ...prev, coverUrl: url }))}
                                            />
                                            <ProfileCard profile={profile} onDirtyChange={setIsDirty}/>
                                        </>
                                    ) : (
                                        <>
                                            <p>У вас пока нет публичного профиля.</p>
                                            <HrnbButton onClick={openPopup} icon={<CreateProfileIcon />}>
                                                Создать профиль
                                            </HrnbButton>
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTab === "my-battles" && (
                                <div className="tab-pane">
                                    <MyBattlePersons publicProfileId={profile?.id || null} />
                                </div>
                            )}
                            {activeTab === "settings" && (
                                <div className="tab-pane">
                                    <div className="settings-section">
                                        <h3>Уведомления</h3>
                                        {notificationsSuccess && (
                                            <HrnbAlert variant="success" onClose={() => setNotificationsSuccess(null)}>
                                                {notificationsSuccess}
                                            </HrnbAlert>
                                        )}
                                        {notificationsError && (
                                            <HrnbAlert variant="danger" onClose={() => setNotificationsError(null)}>
                                                {notificationsError}
                                            </HrnbAlert>
                                        )}
                                        {notificationsLoading ? (
                                            <HrnbLoader text="Загрузка настроек..." />
                                        ) : (
                                            <>
                                                {/* Каналы уведомлений (глобальные переключатели) */}
                                                <div className="settings-notifications">
                                                    <HrnbSwitch
                                                        label="Email уведомления"
                                                        checked={channelSettings.emailNotificationsEnabled}
                                                        onChange={() => handleChannelToggle('emailNotificationsEnabled')}
                                                    />
                                                    <HrnbSwitch
                                                        label="Push уведомления"
                                                        checked={channelSettings.pushNotificationsEnabled}
                                                        onChange={() => handleChannelToggle('pushNotificationsEnabled')}
                                                    />
                                                    <HrnbSwitch
                                                        label="Уведомления в приложении"
                                                        checked={channelSettings.inAppNotificationsEnabled}
                                                        onChange={() => handleChannelToggle('inAppNotificationsEnabled')}
                                                    />
                                                </div>

                                                {/* Гранулярные настройки по типам уведомлений */}
                                                {notificationPreferences.length > 0 && (
                                                    <div className="settings-notification-types">
                                                        {notificationPreferences.map(pref => {
                                                            const channelLabel = {
                                                                EMAIL: 'Email',
                                                                PUSH: 'Push',
                                                                IN_APP: 'В приложении'
                                                            }[pref.channel] || pref.channel;
                                                            const channelEnabled = pref.channel === 'EMAIL'
                                                                ? channelSettings.emailNotificationsEnabled
                                                                : pref.channel === 'PUSH'
                                                                    ? channelSettings.pushNotificationsEnabled
                                                                    : channelSettings.inAppNotificationsEnabled;
                                                            return (
                                                                <details key={pref.channel} className="settings-notification-group" open={!channelEnabled ? undefined : undefined}>
                                                                    <summary className="settings-notification-group__title">
                                                                        {channelLabel} — детальные настройки
                                                                    </summary>
                                                                    <div className="settings-notification-group__types">
                                                                        {(pref.enabledTypes || []).length === 0 && channelEnabled && (
                                                                            <p className="settings-notification-group__empty">
                                                                                Все типы уведомлений отключены для этого канала
                                                                            </p>
                                                                        )}
                                                                        {NOTIFICATION_GROUPS.map(group => (
                                                                            <div key={group.label} className="settings-notification-group__category" style={{ marginBottom: 12 }}>
                                                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#8a9aaa', marginBottom: 6 }}>
                                                                                    {group.label}
                                                                                </div>
                                                                                {group.types.map(type => (
                                                                                    <HrnbSwitch
                                                                                        key={type.value}
                                                                                        label={type.label}
                                                                                        checked={(pref.enabledTypes || []).includes(type.value)}
                                                                                        onChange={() => handleTypeToggle(pref.channel, type.value)}
                                                                                        className="settings-switch--inline"
                                                                                    />
                                                                                ))}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </details>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <HrnbButton
                                            onClick={handleSaveNotifications}
                                            disabled={notificationsSaving || notificationsLoading}
                                            className="settings-save-btn"
                                            icon={<SaveIcon/>}
                                        >
                                            {notificationsSaving ? 'Сохранение...' : 'Сохранить настройки'}
                                        </HrnbButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <CreateProfilePopup show={isOpen} onHide={closePopup} onProfileCreated={updateProfile}/>
        </div>
    );
};

export default Cabinet;